// src/hooks/useIssues.ts — SCE Issues
// Hook de gestion de la liste des problèmes.
//
// Responsabilités :
//  - Charge la liste filtrée (filtres OData côté serveur + complément client)
//  - Injecte automatiquement un filtre sur les identités du déclarant pour les
//    fournisseurs B2B → ils ne voient que leurs propres déclarations.
//    ⚠️ Filtre appliqué côté CLIENT, après téléchargement de la liste : c'est
//    du cloisonnement d'affichage, pas une barrière de sécurité. La vraie
//    barrière est le droit SharePoint accordé à SEC_GLOB_OP_SCE.
//  - Lors d'un createIssue : crée l'item ET poste un commentaire 'Creation' →
//    garantit qu'il y a toujours au moins une entrée timeline pour chaque issue.
//
// ─── Lot 3.5 ───
//  - Le payload ne contient plus famille ni criticité (forcés côté API à
//    'À qualifier').
//  - createIssue récupère TOUS les coordinateurs du métier (cf. cas MECA avec
//    Romuald + Sébastien) via getCoordinateurs() et passe le tableau complet
//    d'UPNs à l'API → le champ multi-valeurs field_15LookupId reçoit tous les
//    LookupIds résolus, et tous les coordinateurs reçoivent la notification
//    Power Automate.

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getIssues,
  createIssue as apiCreateIssue,
  updateIssue as apiUpdateIssue,
} from '@/api/issues.api'
import { addCommentaire } from '@/api/commentaires.api'
import { GraphApiError } from '@/api/graphClient'
import type { Issue, IssueFilters, NewIssuePayload } from '@/types/issue.types'
import { useCurrentUser } from './useCurrentUser'
import { coordinateursDuProbleme } from '@/config/sujets.config'
import { identitesDeclarant } from '@/utils/identiteDeclarant'
import { useSurProblemesModifies } from './signalProblemesModifies'

/**
 * Émis à chaque liste de problèmes chargée (06/10) : la recherche de fond des
 * N° de NC-S (features/reference-ncs) s'en sert, sans que les pages aient à
 * la connaître.
 */
export const EVENEMENT_PROBLEMES_CHARGES = 'sce:problemes-charges'

function annoncerProblemesCharges(issues: readonly Issue[]): void {
  window.dispatchEvent(new CustomEvent<readonly Issue[]>(EVENEMENT_PROBLEMES_CHARGES, { detail: issues }))
}

export interface UseIssuesReturn {
  issues: Issue[]
  loading: boolean
  error: string | null
  refreshIssues: () => Promise<void>
  createIssue: (payload: NewIssuePayload, options?: { ouvreGroupe?: boolean }) => Promise<Issue>
  updateIssue: (itemId: string, partial: Partial<Issue>) => Promise<Issue>
}

/**
 * Formate une erreur Graph API en message utilisateur.
 *  - 403 : permissions Sites.ReadWrite.All non accordées (cas typique IT)
 *  - 401 : session expirée
 *  - autre : message Graph brut
 */
function formatError(err: unknown): string {
  if (err instanceof GraphApiError) {
    if (err.status === 403) {
      return "Droits d'écriture non accordés. Contactez l'IT pour activer Sites.ReadWrite.All."
    }
    if (err.status === 401) {
      return 'Session expirée. Veuillez vous reconnecter.'
    }
    return err.message
  }
  return err instanceof Error ? err.message : 'Erreur inconnue'
}

/**
 * Hook principal pour interagir avec la liste des problèmes SCE.
 * @param filters Filtres serveur (OData) + recherche client.
 *                Pour un fournisseur B2B, le filtre `declarant` est forcé sur
 *                ses identités (UPN + e-mail) et écrase tout filtre entrant.
 */
export function useIssues(filters?: IssueFilters): UseIssuesReturn {
  const [issues, setIssues] = useState<Issue[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const { user, isFournisseur, nomReel } = useCurrentUser()

  // Injection forcée du filtre declarant pour les fournisseurs B2B.
  //
  // Les DEUX identités, pas seulement l'UPN : Author stocke l'e-mail externe
  // du guest, qui ne ressemble en rien à son UPN `#EXT#` (cf.
  // utils/identiteDeclarant). Sur l'UPN seul, un sous-traitant ne voyait
  // aucune fiche — pas même celle qu'il venait de créer.
  const effectiveFilters: IssueFilters | undefined = (() => {
    if (!isFournisseur || !user) return filters
    return { ...(filters ?? {}), declarant: identitesDeclarant(user) }
  })()

  // Sérialisation pour stabilité de useCallback
  const filtersKey = JSON.stringify(effectiveFilters ?? {})

  // Numéro de la dernière requête lancée : seule sa réponse est retenue. Sans
  // ça, un rechargement silencieux parti avant un changement de filtre
  // pouvait revenir APRÈS et réafficher l'ancienne liste.
  const derniereRequete = useRef(0)

  /**
   * @param silencieux relecture en arrière-plan : ni squelette de chargement
   *   (la liste reste affichée, défilement compris), ni message d'erreur à la
   *   place de la liste en cas d'échec — l'ancienne reste, c'est mieux que rien.
   * @returns false si la lecture a échoué (le bouton « Rafraîchir » le dit).
   */
  const chargerIssues = useCallback(async (silencieux: boolean): Promise<boolean> => {
    // Garde : si on doit filtrer par UPN mais que l'user n'est pas encore prêt,
    // on attend plutôt que de charger tous les problèmes en clair.
    if (isFournisseur && !user) {
      setIssues([])
      setLoading(true)
      return true
    }

    const requete = ++derniereRequete.current
    if (!silencieux) {
      setLoading(true)
      setError(null)
    }
    try {
      const list = await getIssues(effectiveFilters)
      if (requete !== derniereRequete.current) return true
      setIssues(list)
      setError(null)
      annoncerProblemesCharges(list)
      return true
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[useIssues] getIssues failed:', err)
      if (requete !== derniereRequete.current) return true
      if (!silencieux) setError(formatError(err))
      return false
    } finally {
      if (requete === derniereRequete.current) setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, isFournisseur, user?.upn])

  const refreshIssues = useCallback(async (): Promise<void> => {
    await chargerIssues(false)
  }, [chargerIssues])

  useEffect(() => {
    void refreshIssues()
  }, [refreshIssues])

  // Fiche refermée après une écriture, ou bouton « Rafraîchir » : relecture
  // silencieuse (cf. signalProblemesModifies).
  const rechargerEnSilence = useCallback(() => chargerIssues(true), [chargerIssues])
  useSurProblemesModifies(rechargerEnSilence)

  /**
   * Crée un problème ET poste son commentaire 'Creation' dans la foulée.
   *
   * Récupère TOUS les coordinateurs du métier (peuvent être plusieurs, ex. MECA
   * avec Romuald + Sébastien) et passe leurs UPNs à l'API → le champ
   * multi-valeurs field_15LookupId reçoit tous les LookupIds résolus.
   *
   * Si le commentaire échoue après une création réussie, on log mais on
   * n'invalide PAS la création — l'issue existe, c'est l'essentiel.
   */
  const createIssue = useCallback(
    async (payload: NewIssuePayload, options?: { ouvreGroupe?: boolean }): Promise<Issue> => {
      if (!user) throw new Error('Utilisateur non authentifié')
      try {
        // Et les responsables du sujet (Goulotte : Thibaut), alertés par le flow 1.
        const coords = coordinateursDuProbleme(payload)
        const coordinateurUPNs = coords.map((c) => c.upn)

        const created = await apiCreateIssue(
          payload,
          { upn: user.upn, nom: user.nom },
          coordinateurUPNs,
          options,
        )

        // Premier événement timeline — best effort, ne bloque pas la déclaration.
        try {
          await addCommentaire({
            problemId: created.id,
            type: 'Creation',
            // Nom du compte RÉEL : c'est lui qui signe (Author), même en
            // bascule de rôle.
            contenu:
              `Problème déclaré par ${nomReel || user.nom}` +
              (payload.groupe ? ` — même défaut que ${payload.groupe}` : ''),
            nouveauStatut: 'Ouvert',
          })
        } catch (commentErr) {
          // eslint-disable-next-line no-console
          console.warn(
            '[useIssues] commentaire de création non posté:',
            commentErr,
          )
        }

        setIssues((prev) => [created, ...prev])
        return created
      } catch (err) {
        throw new Error(formatError(err))
      }
    },
    [user, nomReel],
  )

  const updateIssue = useCallback(
    async (itemId: string, partial: Partial<Issue>): Promise<Issue> => {
      try {
        const updated = await apiUpdateIssue(itemId, partial)
        setIssues((prev) => prev.map((i) => (i.id === updated.id ? updated : i)))
        return updated
      } catch (err) {
        throw new Error(formatError(err))
      }
    },
    [],
  )

  return {
    issues,
    loading,
    error,
    refreshIssues,
    createIssue,
    updateIssue,
  }
}
